import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import type { ReactNode } from 'react'
import { useEffect, useId, useRef } from 'react'
import { createPortal } from 'react-dom'

import { mergeReducedMotionPreset, useThemeMotion } from '../animation/themeMotion'
import { useMediaQuery } from '../hooks/useMediaQuery'
import { ThemeModalCloseButton } from './modal/ThemeModalCloseButton'

type Props = {
  open: boolean
  onClose: () => void
  title: string
  children: ReactNode
  footer?: ReactNode
  /** 像素 / 极客主题：直角与粗描边 */
  angular?: boolean
  /** 宽屏最大宽度 class */
  size?: 'sm' | 'md' | 'lg' | 'xl'
  /** 覆盖可滚动内容区内边距（如紧凑管理列表） */
  bodyClassName?: string
  /**
   * 为 false 时内容区不滚动，由内部子区域自行 `scroll-region`（弹窗内选项卡等固定高度场景）。
   * @default true
   */
  bodyScrollable?: boolean
  /** 面板最小高度 class（弹窗内含选项卡时固定高度，避免切换抖动） */
  panelMinHeightClass?: string
  /**
   * 叠在其它 ThemeModal 之上（更高 z-index），与父弹窗同时 open 时使用。
   * 父层请保持默认 base，避免子层关闭时误触父层逻辑。
   */
  /** `island-overlay`：叠在灵动岛（z-501）之上，用于队列取消确认等 */
  stackLevel?: 'base' | 'nested' | 'island-overlay'
  /**
   * 为 false 时不监听 Escape（嵌套时父层应置 false，仅最上层关闭）。
   * @default true
   */
  closeOnEscape?: boolean
  /**
   * 为 false 时不锁定 document.body 滚动、也不在退出时恢复（嵌套子弹窗请置 false，由最外层弹窗统一管理）。
   * @default true
   */
  manageBodyScroll?: boolean
  /**
   * 为 false 时隐藏关闭按钮、禁用遮罩点击与 Escape 关闭，用于必须等待请求结束的阻塞弹窗。
   * @default true
   */
  dismissible?: boolean
}

/** 移动端底部抽屉贴边全宽；sm+ 再限制最大宽度 */
const sizeClass = {
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-lg',
  lg: 'sm:max-w-2xl',
  xl: 'sm:max-w-6xl',
} as const

const SMASH_BREAKPOINT = '(min-width: 640px)'

/**
 * 全站通用弹窗：使用 CSS 变量（surface / panel / line / accent），随当前主题变化。
 * - 移动端：底部抽屉滑入，顶部圆角
 * - 桌面：居中缩放 + 淡入，与移动端共用同一遮罩过渡（prefers-reduced-motion 仅淡变）
 *
 * 业务代码请从 `components/modal` 统一导入，勿直接引用本文件，便于工程内唯一弹窗入口。
 */
export function ThemeModal({
  open,
  onClose,
  title,
  children,
  footer,
  angular = false,
  size = 'md',
  bodyClassName,
  bodyScrollable = true,
  panelMinHeightClass,
  stackLevel = 'base',
  closeOnEscape = true,
  manageBodyScroll = true,
  dismissible = true,
}: Props) {
  const titleId = useId()
  const instanceKey = useId()
  const wideScreen = useMediaQuery(SMASH_BREAKPOINT)
  const prefersReduced = useReducedMotion()
  const motionPreset = useThemeMotion()
  const preset = mergeReducedMotionPreset(motionPreset, !!prefersReduced)
  const overlayDur = Math.min(0.32, Math.max(0.2, preset.duration * 0.55))
  const panelDur = Math.min(0.42, Math.max(0.24, preset.expandDuration * 0.72))
  const ease = [...preset.easing] as [number, number, number, number]

  const bodyOverflowBackup = useRef<string>('')

  useEffect(() => {
    if (!open || !closeOnEscape || !dismissible) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose, closeOnEscape, dismissible])

  useEffect(() => {
    if (!manageBodyScroll) return
    if (open) {
      bodyOverflowBackup.current = document.body.style.overflow
      document.body.style.overflow = 'hidden'
    }
  }, [open, manageBodyScroll])

  useEffect(() => {
    if (!manageBodyScroll) return
    return () => {
      document.body.style.overflow = bodyOverflowBackup.current ?? ''
    }
  }, [manageBodyScroll])

  const restoreBodyScroll = () => {
    if (!manageBodyScroll) return
    document.body.style.overflow = bodyOverflowBackup.current ?? ''
  }

  if (typeof document === 'undefined') return null

  const reduced = !!prefersReduced

  const panelTransition = reduced
    ? { duration: 0.18, ease: [0.4, 0, 1, 1] as [number, number, number, number] }
    : {
        duration: panelDur,
        ease,
      }
  const backdropTransition = reduced
    ? { duration: 0.16, ease: [0.4, 0, 1, 1] as [number, number, number, number] }
    : { duration: Math.min(0.24, overlayDur), ease }

  const panelHidden = reduced
    ? { opacity: 0 }
    : wideScreen
      ? { opacity: 0, y: 24, scale: 0.98 }
      : { opacity: 0, y: '100%' }

  const panelVisible = {
    opacity: 1,
    y: 0,
    scale: 1,
  }

  const shellRound = angular
    ? 'rounded-none border-2 sm:rounded-none'
    : 'rounded-t-[var(--radius)] border sm:rounded-[var(--radius)]'

  const zShell =
    stackLevel === 'island-overlay'
      ? 'z-[520]'
      : stackLevel === 'nested'
        ? 'z-[210]'
        : 'z-[200]'

  return createPortal(
    <AnimatePresence
      mode="wait"
      onExitComplete={manageBodyScroll ? restoreBodyScroll : undefined}
    >
      {open ? (
        <div
          key={`theme-modal-${instanceKey}`}
          className={[
            'fixed inset-0 flex items-end justify-center sm:items-center sm:p-4',
            zShell,
          ].join(' ')}
          role="presentation"
        >
          <motion.button
            type="button"
            className="theme-modal-backdrop absolute inset-0 cursor-default"
            aria-label="关闭"
            initial={{
              opacity: 0.88,
            }}
            animate={{
              opacity: 1,
            }}
            exit={{
              opacity: 0,
            }}
            transition={backdropTransition}
            style={
              reduced
                ? { backdropFilter: 'none', WebkitBackdropFilter: 'none' }
                : undefined
            }
            onClick={dismissible ? onClose : undefined}
            disabled={!dismissible}
            tabIndex={dismissible ? 0 : -1}
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            initial={panelHidden}
            animate={panelVisible}
            exit={panelHidden}
            transition={panelTransition}
            style={{ transformOrigin: wideScreen ? 'center center' : 'bottom center' }}
            className={[
              'relative z-10 flex w-full flex-col overflow-hidden border-line bg-panel text-ink shadow-card',
              size === 'xl' ? 'max-h-[min(92vh,860px)]' : 'max-h-[min(92vh,720px)]',
              panelMinHeightClass,
              sizeClass[size],
              shellRound,
            ].join(' ')}
          >
            <div
              className={[
                'flex shrink-0 items-start justify-between gap-3 border-b border-line bg-surface-muted px-4 py-3.5 sm:px-5 sm:py-4',
              ].join(' ')}
            >
              <h2
                id={titleId}
                className="text-lg font-bold leading-snug tracking-tight text-ink"
              >
                {title}
              </h2>
              {dismissible ? <ThemeModalCloseButton angular={angular} onClick={onClose} /> : null}
            </div>
            <div
              className={[
                bodyScrollable ? 'scroll-region overflow-y-auto' : 'overflow-hidden',
                'min-h-0 flex-1 bg-surface text-ink',
                bodyClassName ?? 'ps-4 pe-4 py-4 sm:ps-5 sm:pe-6 sm:py-5',
              ].join(' ')}
            >
              {children}
            </div>
            {footer != null ? (
              <div className="shrink-0 border-t border-line bg-surface-muted px-4 py-3 sm:px-5">
                {footer}
              </div>
            ) : null}
          </motion.div>
        </div>
      ) : null}
    </AnimatePresence>,
    document.body,
  )
}
